import { Badge, Button, Group, Modal, Paper, SimpleGrid, Stack, Text, Textarea, TextInput } from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FlowPreviewStepper } from "../../components/FlowTemplatePicker";
import { HiringFlowBuilder } from "../../components/HiringFlowBuilder";
import { LoadingOverlay, PageHeader } from "../../components/ui";
import { companyApi, hiringFlowsApi } from "../../lib/api";
import { ApiError } from "../../lib/apiClient";
import { NAV_COPY, NAV_ICONS } from "../../lib/navCopy";
import { formatDate } from "../../lib/format";
import type { FlowTemplate, StageInput } from "../../types/api";

export function HrTemplatesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [previewTemplate, setPreviewTemplate] = useState<FlowTemplate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<FlowTemplate | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [stages, setStages] = useState<StageInput[]>([
    { name: "Screening", stage_type: "screening", is_mandatory: true },
  ]);

  const companyQuery = useQuery({ queryKey: ["my-company"], queryFn: companyApi.myCompany });

  const templatesQuery = useQuery({
    queryKey: ["hiring-templates", companyQuery.data?.company_id],
    queryFn: () => hiringFlowsApi.listTemplates(companyQuery.data!.company_id),
    enabled: !!companyQuery.data?.company_id,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      hiringFlowsApi.createTemplate(companyQuery.data!.company_id, {
        name: templateName.trim(),
        description: templateDescription.trim() || undefined,
        stages,
      }),
    onSuccess: () => {
      notifications.show({ color: "green", title: "Template created", message: "Your hiring template is ready to use." });
      queryClient.invalidateQueries({ queryKey: ["hiring-templates"] });
      setCreateOpen(false);
      setTemplateName("");
      setTemplateDescription("");
      setStages([{ name: "Screening", stage_type: "screening", is_mandatory: true }]);
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : "Something went wrong";
      notifications.show({ color: "red", title: "Failed", message });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (templateId: string) =>
      hiringFlowsApi.deleteTemplate(companyQuery.data!.company_id, templateId),
    onSuccess: () => {
      notifications.show({ color: "green", title: "Template deleted", message: "The template has been removed." });
      queryClient.invalidateQueries({ queryKey: ["hiring-templates"] });
      setDeleteTarget(null);
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : "Something went wrong";
      notifications.show({ color: "red", title: "Failed", message });
    },
  });

  if (companyQuery.isLoading || templatesQuery.isLoading) {
    return <LoadingOverlay height="var(--axio-unit-300)" />;
  }

  const templates = templatesQuery.data ?? [];

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.hiringFlows}
        title={NAV_COPY.hr.hiringFlows}
        subtitle="Save and reuse hiring flows across job postings."
        actions={
          <Button
            color="violet"
            leftSection={<IconPlus size="var(--axio-unit-16)" />}
            onClick={() => setCreateOpen(true)}
          >
            Create template
          </Button>
        }
      />

      {templates.length === 0 ? (
        <Paper withBorder p="xl">
          <Stack align="center" gap="sm">
            <Text c="dimmed">No company templates yet.</Text>
            <Text size="sm" c="dimmed" ta="center">
              Create a template here or save one from the job wizard when posting a job.
            </Text>
            <Button variant="default" onClick={() => setCreateOpen(true)}>
              Create your first template
            </Button>
          </Stack>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          {templates.map((template) => (
            <Paper key={template.id} withBorder p="md">
              <Stack gap="sm">
                <Group justify="space-between" align="flex-start">
                  <div>
                    <Text fw="var(--axio-font-weight-semibold)">{template.name}</Text>
                    {template.description && (
                      <Text size="sm" c="dimmed" lineClamp={2}>
                        {template.description}
                      </Text>
                    )}
                  </div>
                  <Badge variant="outline" color="gray">
                    {template.stages.length} stage{template.stages.length !== 1 ? "s" : ""}
                  </Badge>
                </Group>
                <Text size="xs" c="dimmed">
                  Created {formatDate(template.created_at)}
                </Text>
                <Group gap="xs">
                  <Button size="xs" variant="default" onClick={() => setPreviewTemplate(template)}>
                    Preview
                  </Button>
                  <Button
                    size="xs"
                    color="violet"
                    onClick={() => navigate(`/hr/jobs/new?templateId=${template.id}`)}
                  >
                    Use for new job
                  </Button>
                  <Button
                    size="xs"
                    variant="subtle"
                    color="red"
                    leftSection={<IconTrash size="var(--axio-unit-14)" />}
                    onClick={() => setDeleteTarget(template)}
                  >
                    Delete
                  </Button>
                </Group>
              </Stack>
            </Paper>
          ))}
        </SimpleGrid>
      )}

      <Modal
        opened={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        title={previewTemplate?.name ?? "Template preview"}
        centered
        size="md"
      >
        {previewTemplate && (
          <Stack gap="md">
            {previewTemplate.description && (
              <Text size="sm" c="dimmed">
                {previewTemplate.description}
              </Text>
            )}
            <FlowPreviewStepper
              stages={previewTemplate.stages.map((s) => ({
                name: s.name,
                stage_type: s.stage_type,
                is_mandatory: s.is_mandatory,
                description: s.description,
              }))}
            />
          </Stack>
        )}
      </Modal>

      <Modal
        opened={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete template?"
        centered
      >
        <Stack>
          <Text size="sm">
            Delete <strong>{deleteTarget?.name}</strong>? This won't affect jobs already using these stages.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={deleteMutation.isPending}
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create hiring template"
        centered
        size="lg"
      >
        <Stack gap="md">
          <TextInput
            label="Template name"
            required
            value={templateName}
            onChange={(e) => setTemplateName(e.currentTarget.value)}
          />
          <Textarea
            label="Description (optional)"
            value={templateDescription}
            onChange={(e) => setTemplateDescription(e.currentTarget.value)}
            minRows={2}
          />
          <HiringFlowBuilder stages={stages} onChange={setStages} />
          <Button
            color="violet"
            loading={createMutation.isPending}
            disabled={templateName.trim().length < 2 || stages.length === 0}
            onClick={() => createMutation.mutate()}
          >
            Create template
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
